import React, { createContext, useContext, useState, ReactNode, useMemo } from 'react';
import { parseSearchQuery, ParsedQuery } from '@/utils/searchQueryParser';

interface SearchContextType {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  clearSearch: () => void;
  activeFilter: string | null;
  setActiveFilter: (filter: string | null) => void;
  parsedQuery: ParsedQuery;
  isStructuredQuery: boolean;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

interface SearchProviderProps {
  children: ReactNode;
}

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  // Parse the search query to detect structured searches
  const parsedQuery = useMemo(() => {
    return parseSearchQuery(searchQuery);
  }, [searchQuery]);

  // Determine if this is a structured query (has model.field patterns)
  const isStructuredQuery = useMemo(() => {
    return parsedQuery.queries.length > 0;
  }, [parsedQuery]);

  const clearSearch = () => {
    setSearchQuery('');
    setActiveFilter(null);
  };

  const value: SearchContextType = {
    searchQuery,
    setSearchQuery,
    clearSearch,
    activeFilter,
    setActiveFilter,
    parsedQuery,
    isStructuredQuery,
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};